import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * A button that opens a small menu of the caller's own buttons (Import ▾, a project's ⋯). Children are rendered as
 * menu items; give them className="menu-item". Esc or a click elsewhere closes it, arrow keys move between items,
 * and clicking an item closes it.
 */
export function MenuButton({ label, children, testId, className = "btn", align = "left", title }: {
  label: ReactNode; children: ReactNode; testId: string; className?: string; align?: "left" | "right"; title?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLButtonElement>('[role="menu"] button:not(:disabled)')?.focus();
    const away = (e: MouseEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const buttons = [...(root.current?.querySelectorAll<HTMLButtonElement>('[role="menu"] button:not(:disabled)') ?? [])];
      if (!buttons.length) return;
      e.preventDefault(); e.stopPropagation();
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(at + (e.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length].focus();
    };
    window.addEventListener("mousedown", away);
    window.addEventListener("keydown", keys, true);
    return () => { window.removeEventListener("mousedown", away); window.removeEventListener("keydown", keys, true); };
  }, [open]);
  return (
    <div className="action-menu" ref={root}>
      <button className={className} aria-haspopup="menu" aria-expanded={open} data-testid={testId} title={title} aria-label={title}
        onClick={() => setOpen(!open)}>{label}</button>
      {open && (
        // A click on an enabled item closes the menu once its own handler has run.
        <div className={`menu menu-${align}`} role="menu"
          onClick={(e) => { const b = (e.target as HTMLElement).closest("button"); if (b && !b.disabled) setOpen(false); }}>
          {children}
        </div>
      )}
    </div>
  );
}
